// Tiny client-side pagination hook. Used wherever a list view wants
// "show N per page, click to advance" without paging the backend.
//
// State machine:
//   - `pageSize` defaults to 5; UI offers 5/10/20/25/30.
//   - `pageIndex` resets to 0 on any pageSize change OR when the
//     incoming items list shrinks below the current page (e.g. filters
//     narrow the result set).
//   - Always exposes `pageCount >= 1` so the UI never shows "Page 1 of 0".
//
// Deliberately pure UI state — does not mirror filter/search inputs.
// Callers wire pagination AFTER their hook's `filteredItems` so a
// keystroke in search resets the page transparently via the
// items-length useEffect below.
import { useCallback, useEffect, useMemo, useState } from "react";

export const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 30] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_PAGE_SIZE: PageSize = 5;

export type PaginationApi<T> = {
  pageSize: PageSize;
  setPageSize: (n: PageSize) => void;
  pageIndex: number;
  setPageIndex: (n: number) => void;
  pageCount: number;
  paginatedItems: T[];
  totalCount: number;
  // Convenience: 1-based label for "Showing X-Y of N" / "Page X of Y" UI.
  firstShown: number;
  lastShown: number;
};

export function usePagination<T>(
  items: T[],
  initialPageSize: PageSize = DEFAULT_PAGE_SIZE
): PaginationApi<T> {
  const [pageSize, setPageSizeState] = useState<PageSize>(initialPageSize);
  const [pageIndex, setPageIndex] = useState(0);

  const totalCount = items.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  // Snap back to a valid page if the list shrank under our feet
  // (filter / search narrowed below the current offset).
  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(0);
  }, [pageIndex, pageCount]);

  const setPageSize = useCallback((n: PageSize) => {
    setPageSizeState(n);
    setPageIndex(0);
  }, []);

  const paginatedItems = useMemo(
    () => items.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [items, pageIndex, pageSize]
  );

  const firstShown = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = Math.min(totalCount, (pageIndex + 1) * pageSize);

  return {
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
    pageCount,
    paginatedItems,
    totalCount,
    firstShown,
    lastShown,
  };
}
